import React from 'react';

import { Field } from 'formik';
import _get from 'lodash/get';
import { DateTime } from 'luxon';
import { makeStyles } from 'tss-react/mui';

import { customFrequencyUtils } from 'app-components/flux/scheduled-transactions';
import type { RecurrenceType } from 'app-components/flux/scheduled-transactions/customFrequencyTypes';
import type { Frequency } from 'app-components/flux/scheduled-transactions/frequency';

import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';

import CustomFrequency from 'components/CustomFrequency/index';
import SDatePicker from 'components/SDatePicker';

import type { STFormikProps } from '../types';

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    height: '500px',
  },
  rightSide: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: theme.spacing(2),
  },
  row: {
    display: 'flex',
    gap: theme.spacing(3),
    '> div:last-of-type': {
      paddingRight: 0,
      '&.MuiButtonBase-root': {
        margin: 0,
      },
    },
  },
  dateWidth: {
    width: '100%',
    '& .MuiFormLabel-root': {
      color: theme.palette.text.secondary,
      fontSize: 14,
    },
  },
  flex: {
    display: 'flex',
  },
  underline: {
    textDecoration: 'underline',
  },
  selectMenuItem: {
    borderRadius: '8px 8px 0px 0px !important',
    '&:hover': {
      borderRadius: '8px !important',
    },
  },
  selectedMenuItem: {
    borderRadius: 8,
  },
  inputLabel: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  formControl: {
    '.MuiFilledInput-root': {
      backgroundColor: theme.palette.background.default,
    },
  },
}));

interface FrequencyTabProps {
  formikProps: STFormikProps;
  minimumDate?: DateTime;
}

function FrequencyTab({ formikProps, minimumDate }: FrequencyTabProps) {
  const { classes } = useStyles();

  const { values, errors } = formikProps;

  const { dueOn, virtualEndOn, virtualFrequency, transaction } = values || {};

  return (
    <div className={classes.root}>
      <div className={classes.rightSide}>
        <Field name='virtualFrequency'>
          {({ field: { ...field } }) => (
            <FormControl variant='filled' className={classes.formControl}>
              <InputLabel className={classes.inputLabel}>Select Frequency</InputLabel>
              <Select
                id='frequency-select'
                // autoFocus={focus === field.name}
                value={virtualFrequency}
                onChange={(event) => {
                  formikProps.setFieldValue(field.name, event.target.value);
                  if (event.target.value === 'One-time payment') {
                    formikProps.setFieldValue('virtualEndOn', undefined);
                  }
                  // setFocus(field.name);
                }}
              >
                {customFrequencyUtils.frequencyAliases.map((item) => {
                  const isSelected = virtualFrequency === item;
                  const splitName = item.includes('X') ? item.split(' ') : item;
                  const needDivider =
                    item === 'One-time payment' ||
                    item === 'Every year' ||
                    item === 'Every quarter' ||
                    item === 'Every X years';
                  return (
                    <MenuItem
                      key={item}
                      value={item}
                      divider={needDivider}
                      sx={{ borderRadius: '8px 8px 0px 0px' }}
                      className={isSelected ? classes.selectedMenuItem : classes.selectMenuItem}
                    >
                      {item.includes('X') ? (
                        <Typography className={classes.flex}>
                          {splitName[0]}&nbsp;<span className={classes.underline}> {splitName[1]} </span>&nbsp;
                          {splitName[2]}
                        </Typography>
                      ) : (
                        item
                      )}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        </Field>

        <Divider />

        {virtualFrequency !== 'One-time payment' && (
          <div className={classes.row}>
            {/* Date Field */}
            <Field name='dueOn'>
              {/*
                // @ts-expect-error FIXME TS7031 */}
              {({ field }) => (
                <SDatePicker
                  // @ts-expect-error FIXME TS2322
                  id='next-date'
                  value={field.value}
                  onChange={(value: DateTime): void => {
                    const updatedValue = value?.isValid ? value : undefined;
                    formikProps.setFieldValue(field.name, updatedValue);
                  }}
                  onError={(startError: string): void => {
                    formikProps.setFieldError(field.name, startError);
                  }}
                  label='Start Date'
                  keyboardShortcutsEnabled={false}
                  className={classes.dateWidth}
                  error={Boolean(_get(errors, field.name))}
                  helperText={_get(errors, field.name)}
                  minDate={minimumDate}
                  maxDate={DateTime.local(2100, 1, 1)}
                />
              )}
            </Field>

            {/* End Date Field */}
            <Field name='virtualEndOn'>
              {/*
                // @ts-expect-error FIXME TS7031 */}
              {({ field }) => (
                <SDatePicker
                  // @ts-expect-error FIXME TS2322
                  id='end-date'
                  value={field.value}
                  onChange={(newDate: DateTime): void => {
                    formikProps.setFieldValue(field.name, newDate);
                  }}
                  onError={(endError: string): void => {
                    formikProps.setFieldError(field.name, endError);
                  }}
                  label='End Date'
                  className={classes.dateWidth}
                  error={Boolean(_get(errors, field.name))}
                  helperText={_get(errors, field.name)}
                  minDate={dueOn || DateTime.local(1900, 1, 1)}
                  maxDate={DateTime.local(2100, 1, 1)}
                  clearable
                />
              )}
            </Field>
          </div>
        )}

        {/* Custom Frequency specification */}
        <div className={classes.row}>
          <Field
            name='virtualRecurrence'
            // @ts-expect-error FIXME TS7006
            validate={(value) => (customFrequencyUtils.isValidRecurrence(value) ? undefined : 'Required')}
          >
            {({ field: { ...field } }) => {
              const currentDueOn: DateTime = dueOn?.startOf('day');
              const currentUntil: DateTime | undefined = virtualEndOn?.startOf('day');
              const currentAlias: string = virtualFrequency;
              const currentFrequency: Frequency = customFrequencyUtils.getFrequencyFromAlias(virtualFrequency);
              const { accountId, amount }: { accountId: string; amount: number } = transaction;
              const error = Object.keys(errors).length > 0;
              const initialState: RecurrenceType = {
                ...field.value,
                alias: virtualFrequency,
                dueOn: currentDueOn,
                endOn: currentUntil,
                frequency: currentFrequency,
              };

              return (
                <CustomFrequency
                  initialState={initialState}
                  currentDueOn={currentDueOn}
                  currentUntil={currentUntil}
                  currentAlias={currentAlias}
                  currentFrequency={currentFrequency}
                  accountId={accountId}
                  amount={amount}
                  error={error}
                  onChange={(virtualRecurrence) => {
                    setTimeout(() => {
                      formikProps.setFieldValue('virtualRecurrence', virtualRecurrence);
                    }, 0);
                  }}
                  onDateChange={(date: DateTime) => {
                    setTimeout(() => {
                      formikProps.setFieldValue('dueOn', date);
                    }, 0);
                  }}
                />
              );
            }}
          </Field>
        </div>
      </div>
    </div>
  );
}

FrequencyTab.displayName = 'FrequencyTab';

export default FrequencyTab;
